import React, { Children, cloneElement, isValidElement } from "react";
import { Link } from "react-router-dom";
import { Button, SectionHeading } from "../../components/ui";
import {
  RevealGroup,
  RevealItem,
  RevealStagger,
} from "../../components/motion/Reveal";
import { PUBLIC_MOTION } from "../../components/motion/publicMotion";

import { useLandingRegistration } from "../../lib/useLandingRegistration.js";

const focusClasses =
  "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-400 focus-visible:ring-offset-2";

const cardLiftClasses =
  "motion-safe:hover:-translate-y-0.5 motion-safe:hover:shadow-md";

const ctaLiftClasses =
  "transition-[color,background-color,border-color,box-shadow,transform] duration-200 motion-safe:hover:-translate-y-0.5 motion-safe:hover:shadow-md";

export function SeoHero({
  eyebrow = "SalesPartner",
  title,
  description,
  primaryCtaLabel = "Registrera er grupp",
  primaryCtaTo = "/register-group",
  secondaryCtaLabel = "Se hur det fungerar",
  secondaryCtaTo = "/sa-fungerar-salespartner",
}) {
  const registration = useLandingRegistration("hero");
  const primaryIsPageAnchor = primaryCtaTo.startsWith("#");
  const primaryLinkProps = primaryIsPageAnchor
    ? { as: "a", href: primaryCtaTo }
    : { as: Link, to: primaryCtaTo === "/register-group" ? registration.to : primaryCtaTo, onClick: primaryCtaTo === "/register-group" ? registration.onClick : undefined };

  return (
    <section className="bg-slate-950 text-white py-14 sm:py-16 md:py-20">
      <RevealGroup
        mode="load"
        stagger={PUBLIC_MOTION.staggerHero}
        delayChildren={PUBLIC_MOTION.delayHero}
        className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8"
      >
        <RevealItem tone="hero" as="p" y={20} className="text-sm font-semibold uppercase tracking-[0.18em] text-amber-300">
          {eyebrow}
        </RevealItem>
        <RevealItem tone="hero" as="h1" y={32} className="mt-4 text-3xl sm:text-4xl md:text-5xl font-bold leading-tight max-w-4xl">
          {title}
        </RevealItem>
        <RevealItem tone="heroSoft" as="p" y={26} className="mt-5 text-white/85 text-base sm:text-lg leading-relaxed max-w-3xl">
          {description}
        </RevealItem>
        <RevealStagger stagger={PUBLIC_MOTION.staggerHero} className="mt-8 flex flex-col sm:flex-row gap-3 sm:items-center">
          <RevealItem tone="heroSoft" y={18} className="w-full sm:w-auto">
            <Button
              {...primaryLinkProps}
              size="lg"
              className={`w-full sm:w-auto justify-center bg-amber-500 hover:bg-amber-400 text-slate-950 ${ctaLiftClasses} ${focusClasses}`}
            >
              {primaryCtaLabel}
            </Button>
          </RevealItem>
          <RevealItem tone="heroSoft" y={18} className="w-full sm:w-auto">
            <Button
              as={Link}
              to={secondaryCtaTo}
              variant="outline"
              size="lg"
              className={`w-full sm:w-auto justify-center border-white/40 text-white hover:bg-white/10 ${ctaLiftClasses} ${focusClasses}`}
            >
              {secondaryCtaLabel}
            </Button>
          </RevealItem>
        </RevealStagger>
      </RevealGroup>
    </section>
  );
}

function TextSectionBody({ children }) {
  const nodes = [];
  let copy = [];

  const flushCopy = (key) => {
    if (!copy.length) return;
    nodes.push(
      <RevealItem key={key} tone="text">
        <div className="space-y-4">{copy}</div>
      </RevealItem>,
    );
    copy = [];
  };

  Children.toArray(children).forEach((child, index) => {
    if (isValidElement(child) && child.type === ContentGrid) {
      flushCopy(`copy-${index}`);
      nodes.push(cloneElement(child, { key: `grid-${index}` }));
      return;
    }
    copy.push(child);
  });
  flushCopy("copy-end");

  return nodes;
}

export function TextSection({ eyebrow, title, intro, children, tone = "white" }) {
  const background = tone === "muted" ? "bg-slate-50" : "bg-white";

  return (
    <section className={`${background} py-12 md:py-16 border-b border-slate-100`}>
      <RevealGroup className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <RevealItem tone="text">
          {eyebrow && (
            <p className="text-sm font-semibold uppercase tracking-[0.16em] text-amber-700">{eyebrow}</p>
          )}
          <h2 className="mt-2 text-2xl md:text-3xl font-bold text-slate-900 max-w-4xl">{title}</h2>
        </RevealItem>
        {intro ? (
          <RevealItem tone="text">
            <p className="mt-4 max-w-3xl text-lg text-slate-700 leading-relaxed">{intro}</p>
          </RevealItem>
        ) : null}
        {children ? (
          <RevealStagger className="mt-6 max-w-4xl space-y-4 text-slate-700 leading-relaxed" stagger={PUBLIC_MOTION.staggerCard}>
            <TextSectionBody>{children}</TextSectionBody>
          </RevealStagger>
        ) : null}
      </RevealGroup>
    </section>
  );
}

export function ContentGrid({ items, columns = 3 }) {
  const gridColumns = columns === 2 ? "md:grid-cols-2" : "md:grid-cols-3";

  return (
    <RevealStagger className={`grid gap-5 ${gridColumns}`} stagger={PUBLIC_MOTION.staggerCard}>
      {items.map((item) => (
        <RevealItem key={item.title} tone="card">
          <article
            className={`rounded-2xl border border-slate-200 bg-white p-5 sm:p-6 shadow-sm transition duration-200 ${cardLiftClasses}`}
          >
            <h3 className="text-lg font-semibold text-slate-900">{item.title}</h3>
            <p className="mt-3 text-slate-700 leading-relaxed">{item.text}</p>
          </article>
        </RevealItem>
      ))}
    </RevealStagger>
  );
}

export function FaqSection({ title = "Vanliga frågor", description, items = [] }) {
  return (
    <section className="bg-slate-50 py-12 md:py-16">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <RevealGroup stagger={PUBLIC_MOTION.staggerFaq} delayChildren={0}>
          <RevealItem tone="text">
            <SectionHeading title={title} description={description} align="left" />
          </RevealItem>
          <RevealStagger className="mt-7 grid gap-4" stagger={PUBLIC_MOTION.staggerFaq}>
            {items.map((item) => (
              <RevealItem key={item.q} tone="faq">
                <details className="group rounded-2xl border border-slate-200 bg-white">
                  <summary
                    className={`flex cursor-pointer list-none items-center gap-3 rounded-2xl px-5 py-4 font-semibold text-slate-900 [&::-webkit-details-marker]:hidden ${focusClasses}`}
                  >
                    <span className="flex-1">{item.q}</span>
                    <svg
                      aria-hidden="true"
                      viewBox="0 0 20 20"
                      fill="currentColor"
                      className="h-5 w-5 shrink-0 text-amber-600 transition-transform duration-200 group-open:rotate-180"
                    >
                      <path
                        fillRule="evenodd"
                        d="M5.23 7.21a.75.75 0 011.06.02L10 11.17l3.71-3.94a.75.75 0 111.08 1.04l-4.25 4.5a.75.75 0 01-1.08 0l-4.25-4.5a.75.75 0 01.02-1.06z"
                        clipRule="evenodd"
                      />
                    </svg>
                  </summary>
                  <div className="space-y-3 px-5 pb-5">
                    <p className="text-slate-700 leading-relaxed">{item.a}</p>
                    {item.to && item.linkLabel ? (
                      <p>
                        <Link
                          to={item.to}
                          className={`font-medium text-amber-800 underline decoration-amber-300 underline-offset-2 hover:text-amber-950 ${focusClasses}`}
                        >
                          {item.linkLabel}
                        </Link>
                      </p>
                    ) : null}
                  </div>
                </details>
              </RevealItem>
            ))}
          </RevealStagger>
        </RevealGroup>
      </div>
    </section>
  );
}

export function SeoLinkGrid({ title = "Läs vidare", links = [] }) {
  return (
    <section className="bg-white py-12 md:py-16">
      <RevealGroup className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <RevealItem tone="text" as="h2" className="text-2xl font-bold text-slate-900">
          {title}
        </RevealItem>
        <RevealStagger className="mt-6 grid sm:grid-cols-2 lg:grid-cols-3 gap-4" stagger={PUBLIC_MOTION.staggerCard}>
          {links.map((link) => (
            <RevealItem key={link.to} tone="card">
              <Link
                to={link.to}
                className={`block rounded-2xl border border-slate-200 bg-slate-50 p-5 font-medium text-slate-800 transition hover:border-amber-300 hover:bg-white hover:shadow-sm ${cardLiftClasses} ${focusClasses}`}
              >
                {link.label}
              </Link>
            </RevealItem>
          ))}
        </RevealStagger>
      </RevealGroup>
    </section>
  );
}
